import { EMPTY_ARRAY } from "@regrello/core-utils";
import type { FormFieldFields, LoadTableQuery, TableFieldInstanceFields } from "@regrello/graphql-api";
import type { GridApi, IRowNode } from "ag-grid-community";
import type { FieldValues } from "react-hook-form";
import { v4 as uuid } from "uuid";
import { SpectrumFieldPluginRegistrar } from "../../../../ui2/shared/molecules/spectrumFields/registry/spectrumFieldPluginRegistrar";
import { getFieldInstanceFormFieldIdOrThrow } from "../../../../utils/customFields/getFieldInstanceFormFieldIdOrThrow";
import type { FrontendSpectrumFormInstance } from "../spectrumFormInstanceUtils";

export type RowDatum = {
  id: string;
  [key: `column-${string}`]: unknown;
};

export type ColumnDefContext =
  | {
      // An empty column that has not yet had a form field assigned.
      type: "undefined";
      displayOrder: number;
    }
  | {
      // A column that corresponds to a defined form field.
      type: "formField";
      formField: FormFieldFields;
      isReadOnly: boolean;
    }
  | undefined;

const COLUMN_FORM_FIELD_PREFIX = "column-form-field-";

const FIELD_INSTANCE_PREFIX = "column-instance-";

export type ColumnIdForFormField = `${typeof COLUMN_FORM_FIELD_PREFIX}${number}`;

/**
 * Creates a column ID for a `RegrelloDataGridV2` column definition. This is what will be returned
 * from `colDef.getColId()` and injected into each cell DOM element's `col-id` attribute.
 *
 * @param formFieldId - The ID of the form field.
 * @returns The column ID for the `RegrelloDataGridV2` column definition.
 */
export function createColumnFormFieldId(formFieldId: number | string): ColumnIdForFormField {
  const resolvedFormFieldId = typeof formFieldId === "string" ? Number.parseInt(formFieldId, 10) : formFieldId;
  return `${COLUMN_FORM_FIELD_PREFIX}${resolvedFormFieldId}`;
}

/**
 * Returns whether the given column ID is a form field column, by checking the structure of its ID.
 * Useful for weeding out internal columns like the row-number column.
 *
 * @param columnId - The column ID to check.
 * @returns Whether the column ID is a form field column.
 */
export function getIsFormFieldColumn(columnId: string): columnId is ColumnIdForFormField {
  return columnId.startsWith(COLUMN_FORM_FIELD_PREFIX);
}

export type ColumnIdForFieldInstance = `${typeof FIELD_INSTANCE_PREFIX}${string}`;

/**
 * Returns whether the given column ID is a field instance column, by checking the structure of its ID.
 * Useful for weeding out internal columns like the row-number column.
 *
 * @param columnId - The column ID to check.
 * @returns Whether the column ID is a field instance column.
 */
export function getIsFieldInstanceColumn(columnId: string): columnId is ColumnIdForFieldInstance {
  return columnId.startsWith(FIELD_INSTANCE_PREFIX);
}

/**
 * Returns the ordered list of column IDs for all form-field columns in the grid.
 * Ignores other possibly present built-in columns like the row-number column.
 *
 * @param api - The AG Grid API
 * @returns An array of column IDs, or an empty array if no columns exist or if the columns couldn't be found.
 */
export function getFormFieldColumnIds(api: GridApi): ColumnIdForFormField[] {
  const columns = api.getColumns();
  if (columns == null) {
    return EMPTY_ARRAY;
  }
  const columnIds = columns.map((column) => column.getId());
  return columnIds.filter((columnId) => getIsFormFieldColumn(columnId));
}

/** Returns a string column ID that is unique within the current workspace. */
export function getColumnIdFromFormFieldId(formFieldId: number): `column-${string}` {
  return createColumnFormFieldId(formFieldId);
}

export function getRowDataFromTableData(data: NonNullable<LoadTableQuery["loadTable"]>) {
  const rowData = new Map<number, RowDatum>();

  for (const fieldInstance of data.tableValues) {
    // Validate that the necessary metadata is present.
    if (fieldInstance.spectrumFieldVersion == null) {
      throw new Error(`Spectrum field version is null for field instance value: ${fieldInstance.values[0].id}`);
    }

    const plugin = SpectrumFieldPluginRegistrar.getPluginForSpectrumField(fieldInstance.spectrumFieldVersion);
    const formFieldId = getFieldInstanceFormFieldIdOrThrow(fieldInstance);
    const columnId = getColumnIdFromFormFieldId(formFieldId);

    const createCell = (rowNumber: number, value: unknown) => ({
      id: rowNumber.toString(),
      [columnId]: value,
    });

    const tabularValues = plugin.getTabularValueForFrontend(fieldInstance);
    // Fill in cells throughout the grid.
    for (const value of tabularValues) {
      const rowDatum = rowData.get(value.rowNumber);
      if (rowDatum == null) {
        rowData.set(value.rowNumber, createCell(value.rowNumber, value.value));
      } else {
        rowDatum[columnId] = value.value;
      }
    }
  }

  return Array.from(rowData.values());
}

/**
 * Retrieves all column IDs from an AG Grid instance.
 *
 * @param api - The AG Grid API
 * @returns An array of column IDs. Returns an empty array if no columns exist.
 */
export function dataGridGetAllColumnIds(api: GridApi) {
  const columns = api.getColumns();
  const columnIds = columns?.map((column) => column.getId()) ?? EMPTY_ARRAY;
  return columnIds;
}

/**
 * Retrieves all row nodes from an AG Grid instance.
 *
 * @param api - The AG Grid API
 * @returns An array of row nodes containing the grid data
 */
// TODOTABLES (clewis): Rename to getRenderedRowNodes
export function dataGridGetAllRows<TFieldValues extends FieldValues>(api: GridApi) {
  const rowNodes: Array<IRowNode<TFieldValues>> = [];
  api.forEachNode((node) => {
    rowNodes.push(node);
  });
  return rowNodes;
}

/**
 * Creates an empty row with the given column field IDs.
 *
 * @param columnFieldIds - The IDs of the columns the empty row must include.
 * @param spectrumFormInstance - The spectrum form instance to use to get the column field instance.
 * @param newRowIndex - The 1-indexed index of the new row.
 * @returns The empty row.
 */
export function createEmptyRow(
  columnFieldIds: number[],
  spectrumFormInstance: FrontendSpectrumFormInstance,
  newRowIndex: number,
) {
  const newRowId = uuid();
  const emptyRow: RowDatum = { id: newRowId };

  // Populate the empty row with the empty tabular value for each column.
  columnFieldIds.forEach((formFieldId) => {
    const { fieldInstance } = spectrumFormInstance.getCompositeFieldInstanceByFormFieldId(formFieldId);

    const { spectrumFieldVersion } = fieldInstance;
    if (spectrumFieldVersion == null) {
      throw new Error(`Spectrum field version is null for form field: ${formFieldId}`);
    }

    const plugin = SpectrumFieldPluginRegistrar.getPluginForSpectrumField(spectrumFieldVersion);
    const emptyTabularValue = plugin.getEmptyTabularValueForFrontend(newRowIndex);

    emptyRow[getColumnIdFromFormFieldId(formFieldId)] = emptyTabularValue.value;
  });

  return emptyRow;
}

/**
 * Returns whether the provided row is empty, meaning it has no data.
 *
 * @returns True if the row has no data in it, or false otherwise
 */
export function getIsRowEmpty({
  columnFormFieldIds,
  row,
  formInstance,
}: {
  /** The ordered list of form-field IDs for each column in the table. */
  columnFormFieldIds: number[];

  /** The materialized Regrello form in which the table lives. */
  formInstance: FrontendSpectrumFormInstance;

  /** A particular row from the table. */
  row: RowDatum;
}) {
  return columnFormFieldIds.every((columnFormFieldId) => {
    return isCellEmpty({ columnFormFieldId, formInstance, row });
  });
}

/**
 * Returns whether the provided cell is empty, meaning it has no data.
 *
 * @returns True if the cell has no data in it, or false otherwise.
 */
export function isCellEmpty({
  columnFormFieldId,
  formInstance,
  row,
}: {
  /** The ordered list of form-field IDs for each column in the table. */
  columnFormFieldId: number;

  /** The materialized Regrello form in which the table lives. */
  formInstance: FrontendSpectrumFormInstance;

  /** A particular row from the table. */
  row: RowDatum;
}) {
  const { fieldInstance } = formInstance.getCompositeFieldInstanceByFormFieldId(columnFormFieldId);

  const { spectrumFieldVersion } = fieldInstance;
  if (spectrumFieldVersion == null) {
    throw new Error(`Spectrum field version is null for form field: ${columnFormFieldId}`);
  }

  const columnPlugin = SpectrumFieldPluginRegistrar.getPluginForSpectrumField(spectrumFieldVersion);
  const columnValue = row[getColumnIdFromFormFieldId(columnFormFieldId)];
  const emptyValue = columnPlugin.getEmptyTabularValueForFrontend(-1).value;

  // (clewis): Consider all null/undefined values to be empty. For some cell types like text which
  // nominally expect an empty string as their empty value, AG Grid will typically emit `null`
  // instead of the empty string when the user deletes the cell value.
  return columnValue == null || columnValue === emptyValue || (Array.isArray(columnValue) && columnValue.length === 0);
}

/**
 * Takes data from the LoadTableQuery and updates the form field ids in it to match the form fields in the
 * spectrum form instance.
 *
 * This is a hack. Tables with columns collected over many tasks, may have duplicate form field ids.
 * To account for this, we modify the FrontendSpectrumFormInstance to have unique form field ids by
 * creating a new form field id for each column. This function maps the data returned from the
 * LoadTableQuery to the generated form fields in the spectrum form instance.
 *
 * If form field columns are unique and we did not modify the FrontendSpectrumFormInstance, this
 * function is a no-op.
 */
export function mapFormFieldsFromFieldInstances({
  spectrumFormInstance,
  tableSectionUuid,
  data,
}: {
  spectrumFormInstance: FrontendSpectrumFormInstance;
  tableSectionUuid: string;
  data: NonNullable<LoadTableQuery["loadTable"]>;
}): NonNullable<LoadTableQuery["loadTable"]> {
  // get form field ids in the potentially modified form instance
  const fieldInstancesOnTable = spectrumFormInstance.getFieldInstancesForSection(tableSectionUuid);

  const fieldInstanceIdToFormFieldId = new Map<number, number>();
  for (const fieldInstance of fieldInstancesOnTable) {
    if (fieldInstance.formFieldID == null) {
      continue;
    }
    fieldInstanceIdToFormFieldId.set(fieldInstance.id, fieldInstance.formFieldID);
  }

  // update the data form field ids
  const newTableValues: TableFieldInstanceFields[] = [];
  for (const tableValue of data.tableValues) {
    if (tableValue.values.length === 0) {
      continue;
    }
    const fieldInstanceId = tableValue.values[0].id;
    const formFieldId = fieldInstanceIdToFormFieldId.get(fieldInstanceId);
    if (formFieldId == null) {
      continue;
    }

    newTableValues.push({
      ...tableValue,
      formFieldID: formFieldId,
    });
  }

  return {
    ...data,
    tableValues: newTableValues,
  };
}
